<script setup>
import { onMounted, onUnmounted, ref, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
  resultsStore.fetchResults(route.params.eventAlias)
}, 30000)
let timer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows
const recentlyChanged = ref({})
let lastResults = []

// Tooltip state
const hoveredCheckpoint = ref(null)
const tooltipPosition = ref({ x: 0, y: 0 })
const isMobile = ref(false)

// Legend popup state
const showLegendPopup = ref(false)

// Computed property to divide markers into stages
const stages = computed(() => {
  if (!resultsStore.markersMap) return []
  
  const markers = Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
  const stages = []
  let currentStage = []
  
  markers.forEach((marker) => {
    // Skip start markers (we don't display them as checkpoints)
    if (marker.type === 1) return
    
    // If this is a transition zone (type 91), finish current stage
    if (marker.type === 91) {
      if (currentStage.length > 0) {
        stages.push({
          markers: currentStage,
          transitionZone: marker
        })
        currentStage = []
      }
    }
    // If this is a finish marker (type 8 or 93), finish current stage
    else if (marker.type === 8 || marker.type === 93) {
      if (currentStage.length > 0) {
        stages.push({
          markers: currentStage,
          finishMarker: marker
        })
        currentStage = []
      }
    }
    // Regular checkpoint - add to current stage
    else {
      currentStage.push(marker)
    }
  })
  
  // If there are remaining markers and no stages yet, create one stage
  if (currentStage.length > 0) {
    stages.push({
      markers: currentStage,
      finishMarker: null
    })
  }
  
  return stages
})

// Get color for checkpoint based on type
function getCheckpointColor(type) {
  switch (type) {
    case 101: return '#dc3545' // red
    case 102: return '#28a745' // green
    case 103: return '#007bff' // blue
    case 104: return '#6f42c1' // purple
    default: return '#fd7e14' // orange
  }
}

// Check if checkpoint is completed by runner
function isCheckpointCompleted(runner, markerId) {
  return runner.splits && runner.splits[markerId] != null
}

// Get stage time for a runner
function getStageTime(runner, stage, stageIndex) {
  // Determine the end marker for this stage
  let endMarker = stage.transitionZone || stage.finishMarker
  
  if (!endMarker) {
    // No end marker, can't calculate time
    return null
  }
  
  // Check if runner reached the end marker
  if (!runner.splits || !runner.splits[endMarker.id]) {
    return null
  }
  
  const endTime = new Date(runner.splits[endMarker.id].time)
  
  // Get start time for this stage
  let startTime
  if (stageIndex === 0) {
    // First stage - use runner start time
    if (!runner.start_time) return null
    startTime = new Date(runner.start_time)
  } else {
    // Not first stage - use previous stage's transition zone time
    const prevStage = stages.value[stageIndex - 1]
    const prevEndMarker = prevStage.transitionZone || prevStage.finishMarker
    if (!prevEndMarker || !runner.splits[prevEndMarker.id]) {
      return null
    }
    startTime = new Date(runner.splits[prevEndMarker.id].time)
  }
  
  const elapsed = endTime - startTime
  return new Date(elapsed)
}

// Get total time for a runner
function getTotalTime(runner) {
  if (runner.finish_time) {
    const finishTime = new Date(runner.finish_time)
    const startTime = new Date(runner.start_time)
    return new Date(finishTime - startTime)
  }
  
  if (runner.finish_time_gps) {
    const finishTime = new Date(runner.finish_time_gps)
    const startTime = new Date(runner.start_time)
    return new Date(finishTime - startTime)
  }
  
  return null
}

// Format time as HH:MM:SS
function getHHMMSSString(date) {
  if (!date) return '--:--:--'
  const timestamp = date instanceof Date ? date.getTime() : date
  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  const dateObj = new Date(timestamp)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

// Get checkpoint time
function getCheckpointTime(runner, markerId) {
  if (!runner.splits || !runner.splits[markerId]) return null
  return new Date(runner.splits[markerId].time)
}

// Handle checkpoint hover
function handleCheckpointHover(event, runner, marker) {
  if (isMobile.value) return // Don't show tooltip on mobile via hover
  
  const time = getCheckpointTime(runner, marker.id)
  if (!time) return
  
  hoveredCheckpoint.value = {
    markerId: marker.id,
    runnerId: runner.id,
    markerText: marker.text,
    time: time.toLocaleTimeString()
  }
  
  tooltipPosition.value = {
    x: event.clientX,
    y: event.clientY
  }
}

function handleCheckpointLeave() {
  if (!isMobile.value) {
    hoveredCheckpoint.value = null
  }
}

// Handle checkpoint click for mobile
function handleCheckpointClick(runner, marker) {
  if (!isMobile.value) return
  
  const time = getCheckpointTime(runner, marker.id)
  if (!time) return
  
  hoveredCheckpoint.value = {
    markerId: marker.id,
    runnerId: runner.id,
    markerText: marker.text,
    time: time.toLocaleTimeString()
  }
}

function closeTooltip() {
  hoveredCheckpoint.value = null
}

// Watch for result changes
import { watch } from 'vue'
watch(
  () => resultsStore.results,
  (newResults) => {
    const nowTime = Date.now()
    if (lastResults.length === 0) {
      lastResults = newResults.map(r => ({ 
        id: r.id, 
        score: r.score, 
        finish_time: r.finish_time, 
        finish_time_gps: r.finish_time_gps, 
        dnf: r.dnf, 
        abs: r.abs 
      }))
      return
    }
    newResults.forEach((r) => {
      const prev = lastResults.find(lr => lr.id === r.id)
      if (!prev || 
          prev.score !== r.score ||
          prev.finish_time !== r.finish_time ||
          prev.finish_time_gps !== r.finish_time_gps ||
          prev.dnf !== r.dnf ||
          prev.abs !== r.abs) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = newResults.map(r => ({ 
      id: r.id, 
      score: r.score, 
      finish_time: r.finish_time, 
      finish_time_gps: r.finish_time_gps, 
      dnf: r.dnf, 
      abs: r.abs 
    }))
  },
  { immediate: true }
)

// Search functionality
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }
  
  const query = searchQuery.value.trim().toLowerCase()
  return resultsStore.results.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return nameMatch || numberMatch
  }).slice(0, 10)
})

watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false
  
  recentlyChanged.value[result.id] = Date.now()
  
  nextTick(() => {
    const resultRow = document.querySelector(`tr[data-result-id="${result.id}"]`)
    if (resultRow) {
      resultRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  })
}

function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
  if (isMobile.value && !event.target.closest('.checkpoint') && !event.target.closest('.tooltip-modal')) {
    hoveredCheckpoint.value = null
  }
  if (!event.target.closest('.legend-button') && !event.target.closest('.legend-popup') && !event.target.classList.contains('legend-backdrop')) {
    showLegendPopup.value = false
  }
}

function toggleLegendPopup() {
  showLegendPopup.value = !showLegendPopup.value
}

function closeLegendPopup() {
  showLegendPopup.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  
  // Detect mobile - prioritize screen width for better tablet handling
  isMobile.value = window.innerWidth < 768
  
  timer = setInterval(() => {
    now.value = Date.now()
    const cutoff = Date.now() - 30 * 1000
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, 1000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}
</script>

<template>
  <div>
    <div class="header-container">
      <h1>Wyniki - Adventure Race</h1>
      
      <!-- Legend button -->
      <button class="legend-button" @click="toggleLegendPopup" title="Pokaż legendę">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 16v-4m0-4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Legenda
      </button>
    </div>
    
    <!-- Legend backdrop and popup -->
    <div v-if="showLegendPopup" class="legend-backdrop" @click="closeLegendPopup"></div>
    <div v-if="showLegendPopup" class="legend-popup">
      <div class="legend-popup-header">
        <h5>Legenda</h5>
        <button class="close-btn" @click="closeLegendPopup">×</button>
      </div>
      <div class="legend-popup-content">
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #dc3545;">■</span>
          <span>Punkt zaliczony</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint empty">□</span>
          <span>Punkt niezaliczony</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #dc3545;">■</span>
          <span>Rower</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #28a745;">■</span>
          <span>Bieg/Trekking</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #007bff;">■</span>
          <span>Kajak</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #6f42c1;">■</span>
          <span>Inna dyscyplina</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled star" style="background-color: #fd7e14;">★</span>
          <span>Zadanie specjalne</span>
        </div>
      </div>
    </div>
    
    <!-- Search functionality -->
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input 
            type="text" 
            v-model="searchQuery" 
            class="form-control search-input" 
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>
        
        <!-- Search dropdown -->
        <div v-if="showDropdown" class="search-dropdown">
          <div 
            v-for="result in filteredResults" 
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="table-scroll">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
            <th>Start</th>
            <th v-for="(stage, index) in stages" :key="index" class="stage-column">
              Etap {{ index + 1 }}
            </th>
            <th>Czas całk.</th>
            <th>Strata</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in resultsStore.results"
            :key="result.id"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            
            <!-- Stage columns -->
            <td v-for="(stage, stageIndex) in stages" :key="stageIndex" class="stage-cell">
              <div class="stage-content">
                <!-- Checkpoints as squares -->
                <div class="checkpoints">
                  <span
                    v-for="marker in stage.markers"
                    :key="marker.id"
                    class="checkpoint"
                    :class="{ 
                      'filled': isCheckpointCompleted(result, marker.id),
                      'empty': !isCheckpointCompleted(result, marker.id),
                      'star': marker.type === 94,
                      'show-text': marker.text && marker.text.length < 4 && marker.type !== 94
                    }"
                    :style="{ 
                      backgroundColor: isCheckpointCompleted(result, marker.id) ? getCheckpointColor(marker.type) : 'transparent',
                      borderColor: getCheckpointColor(marker.type)
                    }"
                    :title="marker.text"
                    @mouseenter="handleCheckpointHover($event, result, marker)"
                    @mouseleave="handleCheckpointLeave"
                    @click="handleCheckpointClick(result, marker)"
                  >
                    <span v-if="marker.type === 94 && isCheckpointCompleted(result, marker.id)" class="star-icon">★</span>
                    <span v-else-if="marker.type === 94 && !isCheckpointCompleted(result, marker.id)" class="star-icon-empty">☆</span>
                    <span v-else-if="marker.text && marker.text.length < 4 && marker.text.length > 0" class="checkpoint-text">{{ marker.text }}</span>
                    <span v-else-if="isCheckpointCompleted(result, marker.id)">■</span>
                    <span v-else>□</span>
                  </span>
                </div>
                
                <!-- Stage time -->
                <div class="stage-time">
                  {{ getStageTime(result, stage, stageIndex) ? getHHMMSSString(getStageTime(result, stage, stageIndex)) : '--:--:--' }}
                </div>
              </div>
            </td>
            
            <!-- Total time -->
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">
                {{ result.abs ? "DNS" : "DNF" }}
              </span>
              <span v-else :class="getResultClass(result)">
                {{ getTotalTime(result) ? getHHMMSSString(getTotalTime(result)) : '--:--:--' }}
              </span>
            </td>
            
            <!-- Gap to leader -->
            <td class="gap-cell">
              <div v-html="formatGapTime(resultsStore.getGapToLeader(result)).replace('\n', '<br/>')"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Tooltip for desktop -->
    <div 
      v-if="hoveredCheckpoint && !isMobile" 
      class="checkpoint-tooltip"
      :style="{ left: tooltipPosition.x + 10 + 'px', top: tooltipPosition.y + 10 + 'px' }"
    >
      <div class="tooltip-content">
        <strong>{{ hoveredCheckpoint.markerText }}</strong><br>
        Czas: {{ hoveredCheckpoint.time }}
      </div>
    </div>
    
    <!-- Modal for mobile -->
    <div v-if="hoveredCheckpoint && isMobile" class="tooltip-modal" @click="closeTooltip">
      <div class="modal-content" @click.stop>
        <button class="close-btn" @click="closeTooltip">×</button>
        <div class="tooltip-content">
          <strong>{{ hoveredCheckpoint.markerText }}</strong><br>
          Czas: {{ hoveredCheckpoint.time }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.result-upcoming {
  font-style: italic;
}

.table {
  text-align: left;
}

.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}

@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

/* Header container */
.header-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

/* Legend button */
.legend-button {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.legend-button:hover {
  background-color: #0056b3;
}

.legend-button svg {
  width: 20px;
  height: 20px;
}

/* Legend popup */
.legend-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 2001;
  min-width: 300px;
  max-width: 400px;
}

/* Legend backdrop */
.legend-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
}

.legend-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
}

.legend-popup-header h5 {
  margin: 0;
  font-size: 1.1em;
}

.legend-popup-header .close-btn {
  position: static;
  padding: 0;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  line-height: 1;
}

.legend-popup-header .close-btn:hover {
  color: #000;
}

.legend-popup-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95em;
}

/* Search styles */
.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  align-items: center;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

/* Stage column */
.stage-column {
  text-align: center;
  min-width: 150px;
}

.stage-cell {
  text-align: center;
  padding: 8px;
}

.stage-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.checkpoints {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: center;
  max-width: 200px;
}

.checkpoint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
}

.checkpoint:hover {
  transform: scale(1.2);
  z-index: 5;
}

.checkpoint.filled {
  color: white;
  font-weight: bold;
}

.checkpoint.empty {
  background-color: white;
  color: #666;
}

.checkpoint.star .star-icon {
  font-size: 14px;
  color: white;
}

.checkpoint.empty .star-icon-empty {
  font-size: 14px;
  color: #666;
}

.checkpoint.show-text .checkpoint-text {
  font-size: 9px;
  font-weight: bold;
  color: white;
}

.checkpoint.empty .checkpoint-text {
  font-size: 9px;
  font-weight: bold;
  color: #666;
}

.stage-time {
  font-size: 0.95em;
  font-weight: 500;
  margin-top: 3px;
}

.gap-cell {
  white-space: pre-line;
  font-size: 0.9em;
}

/* Tooltip */
.checkpoint-tooltip {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  z-index: 1000;
  pointer-events: none;
  max-width: 250px;
}

.tooltip-content {
  line-height: 1.4;
}

/* Mobile modal */
.tooltip-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 300px;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.close-btn:hover {
  color: #000;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .checkpoint {
    width: 18px;
    height: 18px;
    font-size: 11px;
  }
  
  .stage-column {
    min-width: 120px;
  }
  
  .header-container {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .legend-button {
    font-size: 0.85em;
    padding: 6px 12px;
  }
  
  .legend-popup {
    max-width: 90%;
    min-width: 280px;
  }
}
</style>
